import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useU21Form, useU21FormState } from 'app/shared/u21-ui/components';
import {
  flattenValues,
  groupFormState,
} from 'app/modules/filingFormGenerator/formUtils';
import { ValidationErrors } from 'final-form';
import {
  FormValueOrArray,
  FormErrors,
  FormConfig,
} from 'app/modules/filingFormGenerator/models';
import { isObject } from 'lodash';
import { useSubmissionErrors } from 'app/modules/filingFormGenerator/contexts/submissionErrorContext';

interface FlattenedFormErrorContextValue<T> {
  validating: boolean;
  errors: T;
  touched?: Record<string, any>;
  flattenedErrors: Record<string, any>;
  groupedFields: Record<string, Record<string, boolean>>;
  groupedErrors: Record<string, Record<string, string>>;
  rawValidationErrors: FormErrors;
  rawSubmissionErrors: FormErrors;
  groupedTouched: Record<string, Record<string, boolean>>;
  setFieldsTouched: (fields: string[]) => void;
}

const FlattenedFormErrorContext =
  createContext<FlattenedFormErrorContextValue<any> | null>(null);

export const FlattenedFormErrorProvider = <T extends object>({
  children,
  tab,
  formConfig,
}: {
  children: ReactNode;
  tab: string;
  formConfig: FormConfig<Record<string, any>>[];
}) => {
  const [allTouched, setAllTouched] = useState<Record<string, boolean>>({});
  const { submissionErrorMap } = useSubmissionErrors();

  const { errors, touched, values, validating } = useU21FormState<T>({
    subscription: {
      errors: true,
      touched: true,
      values: true,
      validating: true,
    },
  });

  // We want every field that has ever been touched since form initialized to be saved here
  useEffect(() => {
    setAllTouched((prev) => {
      const newTouched = Object.entries(touched ?? {}).reduce(
        (acc, [key, val]) => {
          if (val === true) {
            acc[key] = true;
          }

          return acc;
        },
        {},
      );

      return { ...prev, ...newTouched };
    });
  }, [touched]);

  const {
    mutators: { setFieldTouchedUntyped },
  } = useU21Form<T>();

  const setFieldsTouched = useCallback(
    (fields: string[]) => {
      setAllTouched((prev) => ({
        ...prev,
        ...fields.reduce((acc, f) => ({ ...acc, [f]: true }), {}),
      }));
      fields.forEach((f) => setFieldTouchedUntyped(f, true));
    },
    [setFieldTouchedUntyped],
  );

  useEffect(() => {
    Object.keys(allTouched ?? {}).forEach((f) =>
      setFieldTouchedUntyped(f, true),
    );
  }, [allTouched, setFieldTouchedUntyped, tab]);

  const flattenedErrors = useMemo<Record<string, any>>(
    () => (errors ? flattenValues(errors) : {}),
    [errors],
  );

  const groupedErrors = useMemo(
    () =>
      groupFormState<string, string>(
        flattenedErrors,
        (value) => typeof value === 'string',
      ),
    [flattenedErrors],
  );

  const { rawValidationErrors, rawSubmissionErrors } = useMemo(() => {
    const mountedValidationErrors: FormErrors = [];
    const mountedSubmissionErrors: FormErrors = [];

    formConfig.forEach((tabConfig) => {
      const tabErrors = groupedErrors[tabConfig.tabKey];

      if (isObject(tabErrors)) {
        Object.entries(tabErrors).forEach(([path, error]) => {
          if (submissionErrorMap[path]) {
            mountedSubmissionErrors.push({ path, message: error });
          } else {
            mountedValidationErrors.push({ path, message: error });
          }
        });
      }
    });

    return {
      rawValidationErrors: mountedValidationErrors,
      rawSubmissionErrors: mountedSubmissionErrors,
    };
  }, [formConfig, groupedErrors, submissionErrorMap]);

  const groupedTouched = useMemo(
    () => groupFormState<boolean, boolean>(allTouched),
    [allTouched],
  );

  const groupedFields = useMemo(
    () =>
      groupFormState<FormValueOrArray, boolean>(
        flattenValues(values),
        () => true,
        () => true,
      ),
    [values],
  );

  const contextValue = useMemo(
    () => ({
      validating,
      errors,
      touched: allTouched, // Everything that has been touched since form initialised
      flattenedErrors,
      rawValidationErrors,
      rawSubmissionErrors,
      groupedFields,
      groupedErrors,
      groupedTouched,
      setFieldsTouched,
    }),
    [
      validating,
      errors,
      allTouched,
      flattenedErrors,
      rawValidationErrors,
      rawSubmissionErrors,
      groupedFields,
      groupedErrors,
      groupedTouched,
      setFieldsTouched,
    ],
  );

  return (
    <FlattenedFormErrorContext.Provider value={contextValue}>
      {children}
    </FlattenedFormErrorContext.Provider>
  );
};

export function useFlattenedFormErrors<T extends ValidationErrors>() {
  const context = useContext<FlattenedFormErrorContextValue<T> | null>(
    FlattenedFormErrorContext,
  );
  if (!context) {
    throw new Error(
      'useFlattenedFormErrors must be used within FlattenedFormErrorProvider',
    );
  }

  return context;
}
